<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      style="background: rgba(0,0,0,0.25); backdrop-filter: blur(4px);"
      @click.self="$emit('close')"
    >
      <div class="modal-panel">

        <!-- Header -->
        <div class="modal-header">
          <div>
            <p class="modal-step-label">{{ entryTypeLabel }}</p>
            <h2 class="modal-title">Save to profile</h2>
          </div>
          <button @click="$emit('close')" class="modal-close">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Entry summary -->
        <div class="modal-body">
          <p class="text-sm font-semibold text-zinc-900 dark:text-white truncate">{{ entryTitle }}</p>
          <p v-if="entrySubtitle" class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ entrySubtitle }}</p>
        </div>

        <!-- Options -->
        <div class="px-6 pb-5 flex flex-col gap-3">
          <div
            class="option-card"
            :class="{ 'option-card--selected': mode === 'new' }"
            @click="mode = 'new'"
          >
            <div v-if="mode === 'new'" class="option-check">
              <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug">New profile entry</p>
            <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">Adds as a standalone entry you can reuse across CVs.</p>
          </div>

          <template v-if="hasMasterLink">
            <div
              class="option-card"
              :class="{ 'option-card--selected': mode === 'variant' }"
              @click="mode = 'variant'"
            >
              <div v-if="mode === 'variant'" class="option-check">
                <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug">Alternative framing</p>
              <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">Saves as a variant alongside the original in your profile.</p>
            </div>
            <div v-if="mode === 'variant'" class="field-group">
              <label class="field-label">Variant label</label>
              <input
                v-model="variantLabel"
                class="field-input"
                placeholder="e.g. Finance framing, Shorter version…"
                @click.stop
              />
            </div>
          </template>

          <p v-if="syncError" class="text-xs text-red-500">{{ syncError }}</p>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button @click="$emit('close')" class="wiz-btn-secondary">
            Keep CV only
          </button>
          <button
            @click="handleSave"
            :disabled="isSyncing || (mode === 'variant' && !variantLabel.trim())"
            class="wiz-btn-primary"
          >
            <span v-if="isSyncing" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            <span>{{ isSyncing ? 'Saving…' : 'Save to profile' }}</span>
          </button>
        </div>

      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useSyncToProfile, SYNC_TYPE_CONFIG } from '../composables/useSyncToProfile.js'

export default {
  name: 'SyncToProfileModal',
  props: {
    isOpen: { type: Boolean, default: false },
    entry: { type: Object, default: null },
    entryType: { type: String, default: 'experience' }
  },
  emits: ['close', 'synced'],
  setup(props, { emit }) {
    const { isSyncing, syncError, syncEntry } = useSyncToProfile()
    const mode = ref('new')
    const variantLabel = ref('')

    const config = computed(() => SYNC_TYPE_CONFIG[props.entryType])

    const hasMasterLink = computed(() => {
      if (!props.entry || !config.value) return false
      return !!props.entry[config.value.masterIdField]
    })

    const entryTypeLabel = computed(() => {
      const labels = { experience: 'Experience', education: 'Education', activity: 'Activity' }
      return labels[props.entryType] || props.entryType
    })

    const entryTitle = computed(() => {
      if (!props.entry) return ''
      if (props.entryType === 'experience') return props.entry.jobTitle || 'Untitled'
      if (props.entryType === 'education') return props.entry.institution || 'Untitled'
      if (props.entryType === 'activity') return props.entry.organization || 'Untitled'
      return 'Untitled'
    })

    const entrySubtitle = computed(() => {
      if (!props.entry) return ''
      if (props.entryType === 'experience') return props.entry.company || ''
      if (props.entryType === 'education') return props.entry.programName || ''
      if (props.entryType === 'activity') return props.entry.role || ''
      return ''
    })

    watch(() => props.isOpen, (open) => {
      if (open) {
        mode.value = 'new'
        variantLabel.value = ''
        syncError.value = null
      }
    })

    async function handleSave() {
      if (!props.entry) return
      try {
        const masterEntry = await syncEntry({
          entry: props.entry,
          entryType: props.entryType,
          mode: mode.value,
          variantLabel: variantLabel.value.trim()
        })
        emit('synced', { entry: props.entry, entryType: props.entryType, masterEntry })
      } catch {
        // syncError is set by the composable
      }
    }

    return { mode, variantLabel, hasMasterLink, entryTypeLabel, entryTitle, entrySubtitle, isSyncing, syncError, handleSave }
  }
}
</script>

<style scoped>
.modal-panel {
  @apply bg-white dark:bg-gray-950 border border-zinc-100 dark:border-zinc-800 rounded-2xl w-full max-w-sm flex flex-col overflow-hidden;
}
.modal-header {
  @apply flex items-start justify-between px-6 pt-6 pb-5 border-b border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.modal-step-label {
  @apply text-xs font-semibold uppercase tracking-widest text-gold dark:text-gold-400 mb-1;
}
.modal-title {
  @apply text-lg font-semibold text-zinc-900 dark:text-white;
}
.modal-close {
  @apply p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors;
}
.modal-body {
  @apply px-6 py-5;
}
.modal-footer {
  @apply flex items-center justify-between px-6 py-4 border-t border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.option-card {
  @apply relative border border-zinc-100 dark:border-zinc-800 rounded-xl p-3 cursor-pointer transition-colors hover:border-zinc-300 dark:hover:border-zinc-600;
}
.option-card--selected {
  @apply border-gold dark:border-gold-500 bg-gold-50/40 dark:bg-gold-900/10;
}
.option-check {
  @apply absolute top-2 right-2 w-4 h-4 rounded-full bg-gold flex items-center justify-center flex-shrink-0;
}
.field-group {
  @apply flex flex-col gap-1;
}
.field-label {
  @apply text-xs font-medium text-zinc-500 dark:text-zinc-400;
}
.field-input {
  @apply w-full px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white placeholder-zinc-300 dark:placeholder-zinc-600 focus:outline-none focus:border-primary dark:focus:border-primary-400 transition-colors;
}
.wiz-btn-primary {
  @apply inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity;
}
.wiz-btn-secondary {
  @apply inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors;
}
</style>
